import { PardotCompletionActionReferencesTab } from '@periscopes/general/pardot-completion-actions';
import { PardotField } from '@periscopes/general/pardot-fields';
import { PardotRuleReferencesTab } from '@periscopes/general/pardot-rules';
import { PERISCOPE_TAB_TYPES } from '@sonar-software/sonar-ts-constants';
import { usePeriscopeActions } from '@hooks';

import {
	PardotFieldEmailTab,
	PardotFieldEmailTemplateTab,
	PardotFieldFormTab,
	PardotFieldMetadataTab,
	PardotFieldRelatedTechTab,
	PardotFieldTagsTab
} from '.';
import { PeriscopeBaseComponentProps, PeriscopeBodyComponent } from '@periscopes/types';
import { PeriscopeMenuTabs, PeriscopeTabArea } from '@periscopes/general';
import { PeriscopePardotField, PeriscopePardotFieldSummary, SonarObjectType } from '@sonar-software/sonar-ts-types';
import React, { useCallback, useMemo } from 'react';

type PardotFieldScopeProps = PeriscopeBaseComponentProps<PeriscopePardotFieldSummary>;

export const PardotFieldScope: PeriscopeBodyComponent<PardotFieldScopeProps> = ({ data, index }) => {
	const { openPeriscope } = usePeriscopeActions();
	const { sonarObjectId, relatedCounts = {}, tagIds } = data;
	const fieldTagIds = useMemo(() => tagIds ?? [], [ tagIds ]);
	const tagCount = fieldTagIds?.length ?? 0;

	const handleParentClick = useCallback(
		(field: PeriscopePardotField) => {
			const { parentDisplayName, parentSonarObjectId } = field;

			openPeriscope({
				id: parentSonarObjectId,
				name: parentDisplayName,
				openLeft: true,
				openedFromIndex: index,
				type: SonarObjectType.PardotEntity
			});
		},
		[ openPeriscope, index ]
	);

	return (
		<>
			<PardotField
				field={ data }
				key={ sonarObjectId }
				onParentClick={ handleParentClick }
				showParent />
			<PeriscopeMenuTabs>
				<PeriscopeTabArea
					key={ PERISCOPE_TAB_TYPES.FIELD_METADATA }
					tab={ PERISCOPE_TAB_TYPES.FIELD_METADATA }>
					<PardotFieldMetadataTab
						index={ index }
						picklistValueCount={ relatedCounts[ SonarObjectType.SalesforcePicklistValue ] ?? 0 }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.SalesforceField ] ?? 0 }
					key={ PERISCOPE_TAB_TYPES.RELATED_TECH }
					requiresCount
					tab={ PERISCOPE_TAB_TYPES.RELATED_TECH }>
					<PardotFieldRelatedTechTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts.pardotCompletionActionReference ?? 0 }
					key={ PERISCOPE_TAB_TYPES.COMPLETION_ACTION_REFERENCES }
					requiresCount
					tab={ PERISCOPE_TAB_TYPES.COMPLETION_ACTION_REFERENCES }>
					<PardotCompletionActionReferencesTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts.pardotAutomationRuleReference ?? 0 }
					key={ PERISCOPE_TAB_TYPES.RULE_REFERENCES }
					requiresCount
					tab={ PERISCOPE_TAB_TYPES.RULE_REFERENCES }>
					<PardotRuleReferencesTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.PardotForm ] ?? 0 }
					key={ PERISCOPE_TAB_TYPES.PARDOT_FORMS }
					requiresCount
					tab={ PERISCOPE_TAB_TYPES.PARDOT_FORMS }>
					<PardotFieldFormTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.PardotEmailTemplate ] ?? 0 }
					key={ PERISCOPE_TAB_TYPES.PARDOT_EMAIL_TEMPLATES }
					requiresCount
					tab={ PERISCOPE_TAB_TYPES.PARDOT_EMAIL_TEMPLATES }>
					<PardotFieldEmailTemplateTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ relatedCounts[ SonarObjectType.PardotEmail ] ?? 0 }
					key={ PERISCOPE_TAB_TYPES.PARDOT_EMAILS }
					requiresCount
					tab={ PERISCOPE_TAB_TYPES.PARDOT_EMAILS }>
					<PardotFieldEmailTab
						index={ index }
						sonarObjectId={ sonarObjectId } />
				</PeriscopeTabArea>
				<PeriscopeTabArea
					count={ tagCount }
					key={ PERISCOPE_TAB_TYPES.TAGS }
					requiresCount
					tab={ PERISCOPE_TAB_TYPES.TAGS }>
					<PardotFieldTagsTab
						index={ index }
						tagIds={ fieldTagIds } />
				</PeriscopeTabArea>
			</PeriscopeMenuTabs>
		</>
	);
};
